<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemFmeImportResults') }}</h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchFmeImportResults"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
                class="mb-2"
            >
            </search-box>
            <b-link class="px-1 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilter') }}
            </b-link>
        </div>
        <div>
            <x-checkbox
                :name="`showErrorsOnly`"
                :text="$t('nedglobeServices.fmeImportResults.showErrorsOnly')"
                v-show="dataLoaded"
                v-model="showErrorsOnly"
                class="mb-2"
            >
            </x-checkbox>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                :sticky-header="btableMaxHeight"
                v-show="dataLoaded"
                :items="filteredRecords"
                :fields="fields"
                sort-by="endTime"
                :sort-desc="true"
                sort-icon-left
                class="mb-1"
            >
            </b-table>
        </div>
        <!-- No content for overview -->
        <div
            v-if="(!this.records || this.records.length === 0) && !loading"
            class="col-md-12"
        >
            <span>{{
                $t('nedglobeServices.fmeImportResults.nonePresent')
            }}</span>
        </div>
    </b-container>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';
    import ModalMixin from '../../components/Mixins/ModalMixin.vue';
    import { mapState } from 'vuex';
    import SearchBox from '../../components/SearchBox.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';

    export default {
        name: 'FmeImportResults',

        mixins: [refcodeMixin, ModalMixin],

        components: {
            'search-box': SearchBox,
            'x-checkbox': XCheckbox
        },

        data: function () {
            return {
                fields: [
                    {
                        key: 'filename',
                        label: this.$t(
                            'nedglobeServices.fmeImportResults.filename'
                        ),
                        sortable: true
                    },
                    {
                        key: 'tableName',
                        label: this.$t(
                            'nedglobeServices.fmeImportResults.tableName'
                        ),
                        sortable: true
                    },
                    {
                        key: 'status',
                        label: this.$t('nedglobeServices.fmeImportResults.status'),
                        sortable: true,
                        formatter: (value) => {
                            return this.$t(
                                `nedglobeServices.fmeImportResults.${value}`
                            );
                        }
                    },
                    {
                        key: 'startTime',
                        label: this.$t(
                            'nedglobeServices.fmeImportResults.startTime'
                        ),
                        sortable: true,
                        formatter: (value) => {
                            return new Date(value).toLocaleString(
                                this.$store.state.userInfo.locale
                            );
                        }
                    },
                    {
                        key: 'endTime',
                        label: this.$t('nedglobeServices.fmeImportResults.endTime'),
                        formatter: (value) => {
                            return new Date(value).toLocaleString(
                                this.$store.state.userInfo.locale
                            );
                        },
                        sortable: true
                    },
                    {
                        key: 'result',
                        label: this.$t('nedglobeServices.fmeImportResults.result'),
                        sortable: true,
                        formatter: (value) => {
                            return this.$t(
                                `nedglobeServices.fmeImportResults.${value}`
                            );
                        }
                    },
                    {
                        key: 'resultDetail',
                        label: this.$t(
                            'nedglobeServices.fmeImportResults.resultDetail'
                        ),
                        sortable: true
                    },
                    {
                        key: 'newColumns',
                        label: this.$t(
                            'nedglobeServices.fmeImportResults.newColumns'
                        ),
                        sortable: true
                    },
                    {
                        key: 'removedColumns',
                        label: this.$t(
                            'nedglobeServices.fmeImportResults.removedColumns'
                        ),
                        sortable: true
                    }
                ],
                priorityTypes: null,
                records: [],
                searchText: '',
                btableMaxHeight: `${(window.innerHeight - 300).toString()}px`,
                // loading: during load no test-message shown that there are no messages yet
                loading: true,
                showErrorsOnly: false
            };
        },

        computed: {
            ...mapState(['userInfo']),

            dataLoaded() {
                return this.records.length > 0;
            },

            filteredRecords() {
                let records = this.records;
                if (this.showErrorsOnly) {
                    records = records.filter((record) => record.result === 'Error');
                }

                if (this.searchText && this.searchText.length > 0) {
                    const tokens = this.searchText.toLowerCase();
                    records = records.filter((record) => {
                        return Object.keys(record)
                            .filter((key) => record[key])
                            .some((key) =>
                                record[key]
                                    .toString()
                                    .toLowerCase()
                                    .includes(tokens)
                            );
                    });
                }
                return records;
            }
        },

        async created() {
            nprogress.start();
            this.loading = true;
            // Get all the data from the backend
            await this.$_getData();
            this.loading = false;
            nprogress.done();
        },
        mounted() {
            // to update b-table max-height to have a freeze header (sticky-header makes fixed max-height only regardless the screen height)
            // placed a new issue in Git, see if we get any response.
            this.$nextTick(() => {
                window.addEventListener('resize', () => {
                    // debugger
                    this.btableMaxHeight = `${(
                        window.innerHeight - 300
                    ).toString()}px`; // where offset is some kind of constant margin you need from the top
                });
            });
        },
        beforeDestroy() {
            window.removeEventListener('resize', () => {});
        },

        methods: {
            $_onInput(searchText) {
                this.searchText = searchText;
            },
            $_clearFilters() {
                this.$_onInput('');
            },
            $_clearSelection() {
                this.$_onSelectAll(false);
            },
            $_onSelectAll(event) {
                this.filteredRecords.forEach((record) => {
                    record.selected = event;
                });
            },
            async $_getData() {
                let result = null;
                result = await this.$store.dispatch('useApiData', {
                    name: 'GetFmeImportResults'
                });
                this.records = result?.data;
            }
        },
        watch: {
            // Clear selection when any of the filter items changes
            searchText() {
                this.$_clearSelection();
            },
            showErrorsOnly() {
                this.$_clearSelection();
            }
        }
    };
</script>

